import React, { useEffect, useMemo, useState } from 'react';
import {
  Box,
  Container,
  HStack,
  Heading,
  Icon,
  IconButton,
  Image,
  Link,
  SimpleGrid,
  Text,
  VStack,
  useBreakpointValue
} from '@chakra-ui/react';
import {
  DrawerBackdrop,
  DrawerBody,
  DrawerCloseTrigger,
  DrawerContent,
  DrawerPositioner,
  DrawerRoot
} from '@chakra-ui/react';
import { LuCalendarDays, LuMapPin, LuClock, LuHeart, LuGift, LuUsers, LuPhone, LuBedDouble, LuUtensils, LuFlower2, LuMenu } from 'react-icons/lu';
import { ColorModeButton } from './components/ui/color-mode';
import Game from './components/Game';

function useCountdown(targetDate) {
  const [timeLeft, setTimeLeft] = useState(() => Math.max(0, targetDate - Date.now()));
  useEffect(() => {
    const intervalId = setInterval(() => {
      setTimeLeft(Math.max(0, targetDate - Date.now()));
    }, 1000);
    return () => clearInterval(intervalId);
  }, [targetDate]);
  const seconds = Math.floor(timeLeft / 1000);
  const days = Math.floor(seconds / 86400);
  const hours = Math.floor((seconds % 86400) / 3600);
  const minutes = Math.floor((seconds % 3600) / 60);
  const secs = seconds % 60;
  return { days, hours, minutes, seconds: secs };
}

function Stat({ value, label }) {
  return (
    <VStack spacing={1} minW="80px">
      <Heading size="lg">{value}</Heading>
      <Text fontSize="sm">{label}</Text>
    </VStack>
  );
}

function SectionHeading({ title, children }) {
  return (
    <VStack align="start" spacing={2} mb={6}>
      <Heading size="lg">{title}</Heading>
      {children ? <Text color="semantic.textMuted">{children}</Text> : null}
    </VStack>
  );
}

const NAV_LINKS = [
  { href: '#our-day', label: 'Nasz Dzień' },
  { href: '#venue', label: 'Miejsce' },
  { href: '#noclegi', label: 'Noclegi' },
  { href: '#prezenty', label: 'Prezenty' },
  { href: '#gra', label: 'Gra' },
  { href: '#rsvp', label: 'RSVP' },
];

const CHMURKA_IMAGE_FILENAMES = [
  '626310948_2143642019744837_8463813846759162485_n.jpg',
  '638837901_2338914799907985_1015750655187692457_n.jpg',
  '639508816_1866776280658394_4858843734254254498_n.jpg',
  '640436093_2034216637479868_47823586378787428_n.jpg',
  '635957933_1438154357822224_4901633873230888002_n.jpg',
  '636059748_4107232336229268_133204445125691132_n.jpg',
  '627452494_2261507337673224_4109367597229321618_n.jpg',
  '628124506_1225416379107541_5538279029501205260_n.jpg',
  '627476347_1441208730951151_5353749842371015541_n.jpg',
  '632400622_886507907473702_2847530983066009183_n.jpg',
  '625136243_1527001805039022_7572848473811293716_n.jpg',
  '627493362_1830506287605432_5840899494402094740_n.jpg',
  '625146386_1239548074807254_8701509602838950870_n.jpg',
  '625900327_1654794259015603_3315646466125591972_n.jpg',
  '620085365_668987389575108_1997100885179507897_n.jpg',
  '618366014_1696972331278741_7672917535579039306_n.jpg',
  '617357406_1453814049594123_8080244861511987602_n.jpg',
  '615227027_3348929901939772_6689554104760115578_n.jpg',
  '611014008_1540221140613010_8831769761223031945_n.jpg',
  '614551908_1136869878360818_174719881937578120_n.jpg',
];

function App() {
  const isMobile = useBreakpointValue({ base: true, md: false });
  const [mobileNavOpen, setMobileNavOpen] = useState(false);

  const coupleNames = 'Ania i Kuba';
  const weddingDate = useMemo(() => new Date('2026-07-25T16:00:00Z'), []);
  const weddingCity = 'Niesadna-Przecinka';
  const venueName = 'Sielsko Anielsko';
  const { days, hours, minutes, seconds } = useCountdown(weddingDate.getTime());
  const heroImages = useMemo(() => ([
    `${process.env.PUBLIC_URL}/imgs/80 urodziny babci Teresy_042-1.jpg`,
    `${process.env.PUBLIC_URL}/imgs/IMG_20220629_203917.jpg`,
    `${process.env.PUBLIC_URL}/imgs/IMG_20220808_123425.jpg`,
    `${process.env.PUBLIC_URL}/imgs/PXL_20230121_175555227.jpg`,
    `${process.env.PUBLIC_URL}/imgs/PXL_20250120_130629269.jpg`,
    `${process.env.PUBLIC_URL}/imgs/PXL_20250122_121041426.MP.jpg`,
    `${process.env.PUBLIC_URL}/imgs/PXL_20250330_185440596.jpg`,
    `${process.env.PUBLIC_URL}/imgs/PXL_20250404_153846915.jpg`,
    `${process.env.PUBLIC_URL}/imgs/PXL_20250426_093017686.jpg`,
    `${process.env.PUBLIC_URL}/imgs/PXL_20250621_181908399.jpg`,
  ]), []);

  const chmurkaImages = useMemo(
    () => CHMURKA_IMAGE_FILENAMES.map((name) => `${process.env.PUBLIC_URL}/imgs/chmurka/${name}`),
    []
  );

  const [activeHeroImage, setActiveHeroImage] = useState(0);
  const [activeChmurkaImage, setActiveChmurkaImage] = useState(0);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setActiveHeroImage((prev) => (prev + 1) % heroImages.length);
    }, 4500);
    return () => clearInterval(intervalId);
  }, [heroImages.length]);

  useEffect(() => {
    if (chmurkaImages.length === 0) return;
    const intervalId = setInterval(() => {
      setActiveChmurkaImage((prev) => (prev + 1) % chmurkaImages.length);
    }, 4500);
    return () => clearInterval(intervalId);
  }, [chmurkaImages.length]);

  return (
    <Box bg="semantic.background" color="semantic.text">
      {/* Navigation */}
      <Box position="sticky" top="0" zIndex="100" backdropFilter="saturate(180%) blur(8px)" bg={{ base: 'semantic.surface', _dark: 'semantic.surface' }} borderBottomWidth="1px" borderColor="semantic.border">
        <Container maxW="container.xl" py={3}>
          <HStack justify="space-between">
            <HStack spacing={3}>
              <Icon as={LuHeart} color="brand.500" />
              <Text fontWeight="bold">{coupleNames}</Text>
            </HStack>
            <HStack spacing={2}>
              <HStack spacing={4} display={{ base: 'none', md: 'flex' }}>
                {NAV_LINKS.map(({ href, label }) => (
                  <Link key={href} href={href}>{label}</Link>
                ))}
              </HStack>
              <ColorModeButton aria-label="Toggle color mode" />
              <IconButton
                display={{ base: 'flex', md: 'none' }}
                onClick={() => setMobileNavOpen(true)}
                variant="ghost"
                size="sm"
                aria-label="Otwórz menu"
              >
                <LuMenu />
              </IconButton>
            </HStack>
          </HStack>
        </Container>
      </Box>

      {/* Mobile Navigation Drawer */}
      <DrawerRoot open={mobileNavOpen} onOpenChange={(e) => setMobileNavOpen(e.open)} placement="end" size="xs">
        <DrawerBackdrop />
        <DrawerPositioner>
          <DrawerContent>
            <DrawerCloseTrigger />
            <DrawerBody pt={12}>
              <VStack spacing={6} align="stretch">
                {NAV_LINKS.map(({ href, label }) => (
                  <Link key={href} href={href} fontSize="lg" fontWeight="medium" onClick={() => setMobileNavOpen(false)}>
                    {label}
                  </Link>
                ))}
              </VStack>
            </DrawerBody>
          </DrawerContent>
        </DrawerPositioner>
      </DrawerRoot>

      {/* Hero */}
      <Box as="section" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <SimpleGrid templateColumns={{ base: '1fr', md: '1fr 2fr' }} minH={{ base: 'auto', md: '480px' }} borderRadius="xl" overflow="hidden">
            <Box bg="brand.500" display="flex" alignItems="center" py={{ base: 16, md: 20 }} px={{ base: 6, md: 12 }}>
              <VStack spacing={5} align="start" color="white" maxW="520px">
                <Text fontSize="lg" letterSpacing="widest" textTransform="uppercase" fontWeight="semibold" opacity={0.9}>Pobieramy się!</Text>
                <Heading size={isMobile ? 'xl' : '2xl'} lineHeight="shorter">{coupleNames}</Heading>
                <HStack spacing={4} flexWrap="wrap">
                  <HStack spacing={2}>
                    <Icon as={LuCalendarDays} />
                    <Text>{weddingDate.toLocaleDateString('pl-PL', { month: 'long', day: 'numeric', year: 'numeric' })}</Text>
                  </HStack>
                  <HStack spacing={2}>
                    <Icon as={LuMapPin} />
                    <Text>{weddingCity}</Text>
                  </HStack>
                </HStack>
                <HStack spacing={6} mt={2}>
                  <Stat value={String(days).padStart(2, '0')} label="Dni" />
                  <Stat value={String(hours).padStart(2, '0')} label="Godziny" />
                  <Stat value={String(minutes).padStart(2, '0')} label="Minuty" />
                  <Stat value={String(seconds).padStart(2, '0')} label="Sekundy" />
                </HStack>
              </VStack>
            </Box>
            <Box position="relative" bg="brand.500" minH={{ base: '300px', md: 'auto' }} overflow="hidden">
              {heroImages.map((src, index) => (
                <Image
                  key={src}
                  src={src}
                  alt={`${coupleNames} ${index + 1}`}
                  objectFit="contain"
                  position="absolute"
                  inset="0"
                  w="100%"
                  h="100%"
                  opacity={activeHeroImage === index ? 1 : 0}
                  transition="opacity 700ms ease"
                />
              ))}
            </Box>
          </SimpleGrid>
        </Container>
      </Box>


      {/* RSVP */}
      <Box as="section" id="rsvp" py={{ base: 12, md: 20 }} bg="semantic.muted">
        <Container maxW="container.lg">
          <SectionHeading title="Potwierdź obecność" />
          <Box bg="semantic.cardHighlight" borderRadius="xl" py={{ base: 8, md: 10 }} px={{ base: 6, md: 10 }} textAlign="center">
            <Text mb={2}>Prosimy o potwierdzenie, że będziemy świętować razem!</Text>
            <Text color="semantic.textMuted" mb={6}>Czekamy do 30 maja.</Text>
            <SimpleGrid columns={{ base: 1, sm: 2 }} spacing={4} mb={6} maxW="400px" mx="auto">
              <VStack spacing={1}>
                <HStack spacing={2}><Icon as={LuPhone} color="brand.500" /><Text fontWeight="bold">Ania</Text></HStack>
                <Link href="tel:+48691199605" color="semantic.text">+48 691 199 605</Link>
              </VStack>
              <VStack spacing={1}>
                <HStack spacing={2}><Icon as={LuPhone} color="brand.500" /><Text fontWeight="bold">Kuba</Text></HStack>
                <Link href="tel:+48799880694" color="semantic.text">+48 799 880 694</Link>
              </VStack>
            </SimpleGrid>
            <Text color="semantic.textMuted" fontSize="sm">Wraz z potwierdzeniem prosimy o wskazanie preferencji dietetycznych (menu tradycyjne lub wegetariańskie) oraz wyboru noclegu.</Text>
          </Box>
        </Container>
      </Box>

      {/* Nasz Dzień */}
      <Box as="section" id="our-day" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <SectionHeading title="Nasz Dzień">
          </SectionHeading>
          <SimpleGrid columns={{ base: 1, md: 3 }} spacing={6}>
            <VStack align="start" p={6} borderWidth="1px" borderColor="semantic.border" borderRadius="md" bg="semantic.cardOrange">
              <HStack spacing={2}>
                <Icon as={LuClock} color="accent.orange" />
                <Heading size="sm">Ceremonia</Heading>
              </HStack>
              <Text color="semantic.textMuted">Godzina 16:00 w {venueName}.</Text>
            </VStack>
            <VStack align="start" p={6} borderWidth="1px" borderColor="semantic.border" borderRadius="md" bg="semantic.cardPink">
              <HStack spacing={2}>
                <Icon as={LuUsers} color="brand.500" />
                <Heading size="sm">Wesele</Heading>
              </HStack>
              <Text color="semantic.textMuted">Po ceremonii zapraszamy na wspólne świętowanie, kolację i tańce!</Text>
            </VStack>
            <VStack align="start" p={6} borderWidth="1px" borderColor="semantic.border" borderRadius="md" bg="semantic.cardOrange">
              <HStack spacing={2}>
                <Icon as={LuHeart} color="accent.orange" />
                <Heading size="sm">Nocleg</Heading>
              </HStack>
              <Text color="semantic.textMuted">Zabawa do białego rana! Dla chętnych zapewniamy nocleg i wspólne śniadanie w niedzielę.</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Miejsce */}
      <Box as="section" id="venue" py={{ base: 12, md: 20 }} bg="semantic.muted">
        <Container maxW="container.xl">
          <SectionHeading title="Miejsce" />
          <SimpleGrid columns={{ base: 1, md: 2 }} gap={{ base: 8, md: 16 }} alignItems="stretch">
            <Image
              borderRadius="md"
              objectFit="cover"
              w="100%"
              h="100%"
              minH={{ base: '250px', md: 'auto' }}
              src={`${process.env.PUBLIC_URL}/imgs/sielsko-anielsko.png`}
              alt={venueName}
            />
            <VStack align="start" spacing={4}>
              <Text>
                Ślub i wesele odbędą się w {venueName}.
              </Text>
              <VStack align="start" spacing={3}>
                <HStack>
                  <Icon as={LuMapPin} color="brand.500" />
                  <Text>Niesadna-Przecinka 33, 08-440 Niesadna</Text>
                </HStack>
              </VStack>
              <Box
                as="iframe"
                title="Mapa"
                borderRadius="md"
                borderWidth="1px"
                borderColor="semantic.border"
                w="100%"
                flex={1}
                minH="250px"
                src="https://maps.google.com/maps?q=Sielsko+Anielsko+Niesadna-Przecinka+33&t=&z=13&ie=UTF8&iwloc=&output=embed"
              />
              <Image
                borderRadius="md"
                borderWidth="1px"
                borderColor="semantic.border"
                w="100%"
                src={`${process.env.PUBLIC_URL}/imgs/sielsko_mapka.png`}
                alt={`Mapa ${venueName}`}
              />
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Noclegi */}
      <Box as="section" id="noclegi" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <SectionHeading title="Noclegi" />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack align="start" p={6} borderWidth="1px" borderColor="semantic.border" borderRadius="md" bg="semantic.cardOrange">
              <HStack spacing={2}>
                <Icon as={LuBedDouble} color="accent.orange" />
                <Heading size="sm">Nocleg</Heading>
              </HStack>
              <Text color="semantic.textMuted">Dla chętnych zapewniamy nocleg na miejscu w {venueName}. Daj nam znać przy potwierdzeniu obecności!</Text>
            </VStack>
            <VStack align="start" p={6} borderWidth="1px" borderColor="semantic.border" borderRadius="md" bg="semantic.cardPink">
              <HStack spacing={2}>
                <Icon as={LuUtensils} color="brand.500" />
                <Heading size="sm">Śniadanie w niedzielę</Heading>
              </HStack>
              <Text color="semantic.textMuted">W niedzielę rano zapraszamy na wspólne śniadanie — doskonała okazja, żeby jeszcze trochę poświętować razem.</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Prezenty */}
      <Box as="section" id="prezenty" py={{ base: 12, md: 20 }} bg="semantic.muted">
        <Container maxW="container.xl">
          <SectionHeading title="Prezenty" />
          <SimpleGrid columns={{ base: 1, md: 2 }} spacing={6}>
            <VStack p={6} align="start" borderWidth="1px" borderColor="semantic.border" borderRadius="md" bg="semantic.cardPink">
              <HStack spacing={2}>
                <Icon as={LuGift} color="brand.500" />
                <Heading size="sm">Wsparcie pieniężne</Heading>
              </HStack>
              <Text color="semantic.textMuted">Jeżeli chcielibyście coś nam podarować — mile widziane będzie wsparcie w formie pieniężnej.</Text>
            </VStack>
            <VStack p={6} align="start" borderWidth="1px" borderColor="semantic.border" borderRadius="md" bg="semantic.cardOrange">
              <HStack spacing={2}>
                <Icon as={LuFlower2} color="accent.orange" />
                <Heading size="sm">Zamiast kwiatów</Heading>
              </HStack>
              <Text color="semantic.textMuted">Zamiast obdarowywania nas kwiatami zachęcamy do wsparcia fundacji <Link href="https://www.malibracia.org.pl" target="_blank" fontWeight="bold" color="brand.600">Mali Bracia Ubogich</Link> — skarbonka będzie w trakcie składania życzeń.</Text>
            </VStack>
          </SimpleGrid>
        </Container>
      </Box>

      {/* Gra */}
      <Box as="section" id="gra" py={{ base: 12, md: 20 }}>
        <Container maxW="container.xl">
          <Game />
        </Container>
      </Box>

      {/* Chmurka Hero */}
      {chmurkaImages.length > 0 && (
        <Box as="section" py={{ base: 12, md: 20 }}>
          <Container maxW="container.xl">
            <SimpleGrid templateColumns={{ base: '1fr', md: '1fr 2fr' }} minH={{ base: 'auto', md: '480px' }} borderRadius="xl" overflow="hidden">
              <Box bg="accent.orange" display="flex" alignItems="center" py={{ base: 16, md: 20 }} px={{ base: 6, md: 12 }}>
                <VStack spacing={5} align="start" color="white" maxW="520px">
                  <Text fontSize="lg" letterSpacing="widest" textTransform="uppercase" fontWeight="semibold" opacity={0.9}>Chmurka</Text>
                  <Text>Skoro już przejrzeliście całą stronę, możecie obejrzeć zdjęcia Chmurki ;)</Text>
                </VStack>
              </Box>
              <Box position="relative" bg="accent.orange" minH={{ base: '300px', md: 'auto' }} overflow="hidden">
                {chmurkaImages.map((src, index) => (
                  <Image
                    key={src}
                    src={src}
                    alt={`Chmurka ${index + 1}`}
                    objectFit="contain"
                    position="absolute"
                    inset="0"
                    w="100%"
                    h="100%"
                    opacity={activeChmurkaImage === index ? 1 : 0}
                    transition="opacity 700ms ease"
                  />
                ))}
              </Box>
            </SimpleGrid>
          </Container>
        </Box>
      )}

      {/* Footer */}
      <Box as="footer" py={8} borderTopWidth="1px" borderColor="semantic.border">
        <Container maxW="container.xl">
          <VStack spacing={3}>
            <Icon as={LuHeart} color="brand.500" boxSize={5} />
            <Text fontWeight="medium">{coupleNames}</Text>
            <Text color="semantic.textMuted" fontSize="sm">25 lipca 2026 · {weddingCity}</Text>
            <Link href="https://ania-kuba.wedding" color="semantic.textMuted" fontSize="sm">ania-kuba.wedding</Link>
          </VStack>
        </Container>
      </Box>
    </Box>
  );
}

export default App;
